"use client";

import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useInView, useReducedMotion } from "framer-motion";
import { stegaClean } from "@sanity/client/stega";
import type {
  PagePricingSchema,
  PricingAvailability,
  PricingCompareRow,
} from "@/sanity/pages/PagePricingTemplate";
import { Reveal, SectionHeading } from "../ai-features/ui";
import styles from "./pricing.module.scss";

const EASE = "cubic-bezier(0.16, 1, 0.3, 1)";

function Mark({
  kind,
  text,
  labels,
  muted,
}: {
  kind?: PricingAvailability;
  text?: string;
  labels: { included?: string; limited?: string; notOffered?: string };
  muted?: boolean;
}) {
  if (kind === "text") return <span data-muted={muted}>{text}</span>;
  const label =
    kind === "limited"
      ? labels.limited
      : kind === "none"
        ? labels.notOffered
        : labels.included;
  return (
    <i
      className={styles.mark}
      data-kind={kind ?? "included"}
      role="img"
      aria-label={label ?? kind}
    />
  );
}

function Row({
  row,
  index,
  labels,
}: {
  row: PricingCompareRow;
  index: number;
  labels: { included?: string; limited?: string; notOffered?: string };
}) {
  return (
    <div
      className={styles.row}
      role="row"
      style={{ "--i": index } as CSSProperties}
    >
      <span role="rowheader">{row.label}</span>
      <span role="cell" data-col="tradestats">
        <Mark kind={row.tradestats} text={row.tradestatsText} labels={labels} />
      </span>
      <span role="cell" data-col="others">
        <Mark kind={row.others} text={row.othersText} labels={labels} muted />
      </span>
    </div>
  );
}

export default function CompareSection({
  data,
}: {
  data?: PagePricingSchema["compare"];
}) {
  const [expanded, setExpanded] = useState(false);
  const tableId = useId();
  const sectionRef = useRef<HTMLElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const heightBefore = useRef<number | null>(null);
  const reducedMotion = useReducedMotion();
  const inView = useInView(sectionRef, { once: true, margin: "0px 0px -15% 0px" });

  // FLIP the table height between the collapsed and expanded row sets.
  useLayoutEffect(() => {
    const body = bodyRef.current;
    const from = heightBefore.current;
    heightBefore.current = null;
    if (!body || from === null) return;
    const to = body.offsetHeight;
    if (!expanded && sectionRef.current) {
      // Collapsing from the table's end would drop the reader into the next
      // section: keep the heading on screen.
      const top = sectionRef.current.getBoundingClientRect().top;
      if (top < 0) {
        window.scrollTo({
          top: window.scrollY + top,
          behavior: reducedMotion ? "auto" : "smooth",
        });
      }
    }
    if (reducedMotion || from === to) return;
    body.style.overflow = "hidden";
    const animation = body.animate(
      [{ height: `${from}px` }, { height: `${to}px` }],
      { duration: 650, easing: EASE },
    );
    const done = () => {
      body.style.overflow = "";
    };
    animation.onfinish = done;
    return () => {
      animation.cancel();
      done();
    };
  }, [expanded, reducedMotion]);

  if (!data) return null;
  const categories = data.categories ?? [];
  const featured = categories.flatMap((category) =>
    (category.rows ?? []).filter((row) => row.featured),
  );
  const labels = {
    included: data.includedLabel,
    limited: data.limitedLabel,
    notOffered: data.notOfferedLabel,
  };
  const toggleLabel = stegaClean(
    expanded ? data.showLessLabel : data.showAllLabel,
  );
  const toggle = () => {
    heightBefore.current = bodyRef.current?.offsetHeight ?? null;
    setExpanded((prev) => !prev);
  };
  let rowIndex = 0;
  return (
    <section
      ref={sectionRef}
      className={styles.compare}
      data-expanded={expanded}
      data-in-view={inView}
    >
      <SectionHeading data={{ label: data.label, title: data.title }} />
      <Reveal className={styles.tableWrap}>
        <div ref={bodyRef} className={styles.tableBody}>
          <div
            className={styles.table}
            role="table"
            id={tableId}
            aria-label={stegaClean(data.title)}
          >
            <div className={styles.tableHead} role="row">
              <span role="columnheader" />
              <span role="columnheader" data-col="tradestats">
                <b>{data.tradestatsLabel}</b>
                <span>
                  {data.tradestatsOriginalPrice && (
                    <s>{data.tradestatsOriginalPrice}</s>
                  )}
                  <em>{data.tradestatsPrice}</em>
                </span>
              </span>
              <span role="columnheader" data-col="others">
                <b>{data.othersLabel}</b>
                <span>{data.othersPrice}</span>
              </span>
            </div>
            {expanded
              ? categories.map((category) => (
                  <div key={category._key} role="rowgroup">
                    <div
                      className={styles.category}
                      role="row"
                      style={{ "--i": rowIndex++ } as CSSProperties}
                    >
                      <span role="rowheader">{category.title}</span>
                    </div>
                    {category.rows?.map((row) => (
                      <Row
                        key={row._key}
                        row={row}
                        index={rowIndex++}
                        labels={labels}
                      />
                    ))}
                  </div>
                ))
              : featured.map((row) => (
                  <Row
                    key={row._key}
                    row={row}
                    index={rowIndex++}
                    labels={labels}
                  />
                ))}
          </div>
          {!expanded && (
            <span className={styles.tableFade} aria-hidden="true" />
          )}
        </div>
      </Reveal>
      {expanded && (data.includedLabel || data.limitedLabel) && (
        <ul className={styles.legend}>
          <li>
            {data.includedLabel}
            <i className={styles.mark} data-kind="included" aria-hidden="true" />
          </li>
          <li>
            <i className={styles.mark} data-kind="limited" aria-hidden="true" />
            {data.limitedLabel}
          </li>
          <li>
            <i className={styles.mark} data-kind="none" aria-hidden="true" />
            {data.notOfferedLabel}
          </li>
        </ul>
      )}
      {toggleLabel && (
        <button
          type="button"
          className={styles.expand}
          aria-expanded={expanded}
          aria-controls={tableId}
          onClick={toggle}
        >
          {toggleLabel}
          <i aria-hidden="true" />
        </button>
      )}
      {data.footnote && <p className={styles.footnote}>{data.footnote}</p>}
    </section>
  );
}
